import { create } from "zustand";

export type ToastVariant = "success" | "error" | "warning" | "info" | "loading";

export type ToastPosition =
  | "top-right"
  | "top-left"
  | "top-center"
  | "bottom-right"
  | "bottom-left"
  | "bottom-center";

/** 1 = en yüksek öncelik, 5 = en düşük */
export type ToastPriority = 1 | 2 | 3 | 4 | 5;

export const TOAST_POSITIONS: readonly ToastPosition[] = [
  "top-left",
  "top-center",
  "top-right",
  "bottom-left",
  "bottom-center",
  "bottom-right",
];

export interface ToastAction {
  label: string;
  action: () => void;
  style?: "primary" | "secondary" | "destructive";
}

export interface Toast {
  id: string;
  type: ToastVariant;
  title?: string;
  message: string;
  /** ms cinsinden; 0 => kullanıcı kapatana kadar ekranda kalır */
  duration: number;
  dismissible: boolean;
  actions?: ToastAction[];
  priority: ToastPriority;
  /** loading toast'ları için dışarıdan beslenen 0-100 arası ilerleme */
  progress?: number;
  /**
   * message'ı HTML olarak render eder. Yalnızca uygulama içinde üretilen sabit
   * içerik için kullanılmalı — backend mesajı veya kullanıcı girdisi ASLA.
   */
  html?: boolean;
  position?: ToastPosition;
  data?: unknown;
  createdAt: number;
  /** Değiştiğinde toast bileşeni otomatik kapanma sayacını sıfırlar */
  updatedAt: number;
  /** Kuyruğa alındığı an; TTL hesabı için kullanılır */
  queuedAt?: number;
}

export interface ToastInput {
  /**
   * Sabit bir anahtar verildiğinde aynı id'ye sahip toast yenisi eklenmek
   * yerine güncellenir. Tekrarlanan işlemler için tek bir toast'ı tazelemeyi
   * sağlar: error(msg, { id: "profile-save" })
   */
  id?: string;
  type?: ToastVariant;
  title?: string;
  message: string;
  duration?: number;
  dismissible?: boolean;
  actions?: ToastAction[];
  priority?: ToastPriority;
  progress?: number;
  html?: boolean;
  position?: ToastPosition;
  data?: unknown;
}

export type ToastOptions = Omit<ToastInput, "message" | "type">;

export type ToastUpdate = Partial<
  Omit<Toast, "id" | "createdAt" | "updatedAt" | "queuedAt">
>;

export interface ToastApi {
  addToast: (input: ToastInput) => string;
  removeToast: (id: string) => void;
  updateToast: (id: string, updates: ToastUpdate) => void;
  clearAllToasts: () => void;

  success: (message: string, options?: ToastOptions) => string;
  error: (message: string, options?: ToastOptions) => string;
  warning: (message: string, options?: ToastOptions) => string;
  info: (message: string, options?: ToastOptions) => string;
  loading: (message: string, options?: ToastOptions) => string;

  promise: <T>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: unknown) => string);
    },
    options?: ToastOptions
  ) => Promise<T>;

  setPosition: (position: ToastPosition) => void;
  setMaxToasts: (max: number) => void;
}

interface ToastState {
  toasts: Toast[];
  /** maxToasts aşıldığında bekleyen toast'lar */
  queue: Toast[];
  position: ToastPosition;
  maxToasts: number;
  /** Kuyrukta bu süreden fazla bekleyen toast'lar gösterilmeden düşer (ms) */
  queueTtl: number;
}

type ToastStore = ToastState & ToastApi;

const DEFAULT_DURATIONS: Record<ToastVariant, number> = {
  success: 4000,
  error: 6000,
  warning: 5000,
  info: 4000,
  loading: 0,
};

const PRIORITY_BY_VARIANT: Record<ToastVariant, ToastPriority> = {
  error: 1,
  warning: 2,
  loading: 2,
  success: 3,
  info: 4,
};

/**
 * Açık bir id verilmediğinde, bu süre içinde gelen aynı metin + tip
 * kombinasyonu yeni toast açmak yerine mevcut olanı tazeler.
 */
const IMPLICIT_DEDUPE_WINDOW = 2500;

let toastCounter = 0;
const generateId = () => `toast-${Date.now()}-${++toastCounter}`;

/** Görünen listedeki en zayıf (en düşük öncelikli, en eski) toast'ın indeksi */
const weakestVisibleIndex = (toasts: Toast[]): number =>
  toasts.reduce((weakest, toast, index) => {
    const current = toasts[weakest];
    if (toast.priority > current.priority) return index;
    if (toast.priority === current.priority && toast.createdAt < current.createdAt) {
      return index;
    }
    return weakest;
  }, 0);

/**
 * Boş slot varsa kuyruktan toast terfi ettirir ve süresi geçmiş kuyruk
 * kayıtlarını düşürür. Saf fonksiyon: set() içinde doğrudan kullanılabilir.
 */
const drainQueue = (state: ToastState): Pick<ToastState, "toasts" | "queue"> => {
  const now = Date.now();

  const alive = state.queue
    .filter((toast) => now - (toast.queuedAt ?? toast.createdAt) <= state.queueTtl)
    .sort((a, b) => a.priority - b.priority || a.createdAt - b.createdAt);

  const slots = state.maxToasts - state.toasts.length;
  if (slots <= 0) {
    return { toasts: state.toasts, queue: alive };
  }

  // Terfi eden toast hiç görünmediği için sayacı terfi anından başlar.
  const promoted = alive.slice(0, slots).map((toast) => ({
    ...toast,
    queuedAt: undefined,
    updatedAt: now,
  }));

  return {
    toasts: [...state.toasts, ...promoted],
    queue: alive.slice(slots),
  };
};

const findExisting = (state: ToastState, input: ToastInput): Toast | undefined => {
  if (input.id) {
    return (
      state.toasts.find((toast) => toast.id === input.id) ??
      state.queue.find((toast) => toast.id === input.id)
    );
  }

  const type = input.type ?? "info";

  // loading toast'ları belirli bir işlemi temsil eder; aynı metinle çalışan iki
  // eşzamanlı işlem tek kutuda birleşirse biri bittiğinde diğerinin bildirimi de
  // kapanır. Bu yüzden örtük birleştirmeden muaf tutuluyorlar.
  if (type === "loading") return undefined;

  const now = Date.now();

  return state.toasts.find(
    (toast) =>
      toast.message === input.message &&
      toast.type === type &&
      now - toast.updatedAt < IMPLICIT_DEDUPE_WINDOW
  );
};

export const useToastStore = create<ToastStore>()((set, get) => ({
  toasts: [],
  queue: [],
  position: "bottom-right",
  maxToasts: 5,
  queueTtl: 10000,

  addToast: (input) => {
    const existing = findExisting(get(), input);

    // Aynı toast tekrar tetiklendiğinde yeni kutu açmak yerine mevcut olanı
    // tazele — updatedAt değişimi bileşende sayacı da sıfırlar.
    if (existing) {
      get().updateToast(existing.id, {
        type: input.type ?? existing.type,
        title: input.title,
        message: input.message,
        duration: input.duration ?? existing.duration,
        dismissible: input.dismissible ?? existing.dismissible,
        actions: input.actions,
        priority: input.priority ?? existing.priority,
        progress: input.progress,
        html: input.html,
        position: input.position ?? existing.position,
        data: input.data,
      });
      return existing.id;
    }

    const now = Date.now();
    const type = input.type ?? "info";

    const toast: Toast = {
      ...input,
      id: input.id ?? generateId(),
      type,
      duration: input.duration ?? DEFAULT_DURATIONS[type],
      dismissible: input.dismissible ?? true,
      priority: input.priority ?? PRIORITY_BY_VARIANT[type],
      createdAt: now,
      updatedAt: now,
    };

    set((state) => {
      // Görünen toast'ların sırası hiç değişmez: yenisi her zaman sona eklenir.
      // Öncelik yalnızca kapasite dolduğunda kimin bekleyeceğine karar verir.
      if (state.toasts.length < state.maxToasts) {
        return { toasts: [...state.toasts, toast] };
      }

      const weakestIndex = weakestVisibleIndex(state.toasts);
      const weakest = state.toasts[weakestIndex];

      if (!weakest || toast.priority >= weakest.priority) {
        return { queue: [...state.queue, { ...toast, queuedAt: now }] };
      }

      // Daha kritik bir toast geldi: en zayıf olanı kuyruğun başına alıyoruz ki
      // yer açıldığında ilk o geri gelsin.
      const visible = [...state.toasts];
      visible.splice(weakestIndex, 1);

      return {
        toasts: [...visible, toast],
        queue: [{ ...weakest, queuedAt: now }, ...state.queue],
      };
    });

    return toast.id;
  },

  removeToast: (id) => {
    set((state) => {
      const toasts = state.toasts.filter((toast) => toast.id !== id);
      const queue = state.queue.filter((toast) => toast.id !== id);

      if (toasts.length === state.toasts.length && queue.length === state.queue.length) {
        return state;
      }

      return drainQueue({ ...state, toasts, queue });
    });
  },

  updateToast: (id, updates) => {
    set((state) => {
      const apply = (toast: Toast): Toast =>
        toast.id === id ? { ...toast, ...updates, updatedAt: Date.now() } : toast;

      return {
        toasts: state.toasts.map(apply),
        queue: state.queue.map(apply),
      };
    });
  },

  clearAllToasts: () => set({ toasts: [], queue: [] }),

  success: (message, options) =>
    get().addToast({ ...options, type: "success", message }),

  error: (message, options) => get().addToast({ ...options, type: "error", message }),

  warning: (message, options) =>
    get().addToast({ ...options, type: "warning", message }),

  info: (message, options) => get().addToast({ ...options, type: "info", message }),

  loading: (message, options) =>
    get().addToast({
      ...options,
      type: "loading",
      message,
      duration: options?.duration ?? 0,
      // Takılan bir isteğin toast'ı ekranda kilitli kalmasın diye kapatılabilir.
      dismissible: options?.dismissible ?? true,
    }),

  promise: async (promise, messages, options) => {
    const id = get().loading(messages.loading, options);

    const settle = (variant: "success" | "error", message: string) => {
      // Kullanıcı loading toast'ını kapattıysa updateToast no-op olur;
      // kapatılan bildirim geri gelmez.
      get().updateToast(id, {
        type: variant,
        message,
        duration: DEFAULT_DURATIONS[variant],
        priority: PRIORITY_BY_VARIANT[variant],
        dismissible: true,
        progress: undefined,
      });
    };

    try {
      const result = await promise;
      settle(
        "success",
        typeof messages.success === "function"
          ? messages.success(result)
          : messages.success
      );
      return result;
    } catch (promiseError) {
      settle(
        "error",
        typeof messages.error === "function"
          ? messages.error(promiseError)
          : messages.error
      );
      throw promiseError;
    }
  },

  setPosition: (position) => set({ position }),

  setMaxToasts: (maxToasts) =>
    set((state) => ({ maxToasts, ...drainQueue({ ...state, maxToasts }) })),
}));
